import { HttpErrorResponse } from '@angular/common/http';
import { VAGA_SUSPENSA_CODIGO, VAGA_SUSPENSA_MENSAGEM } from './vaga-suspensa-error';

export interface BackendFieldError {
  field: string;
  message: string;
}

export interface BackendErrorMessage {
  title: string;
  detail: string;
  traceId?: string;
  fieldErrors?: BackendFieldError[];
}

const NETWORK_ERROR: BackendErrorMessage = {
  title: 'Falha de conexão',
  detail: 'Verifique sua internet e tente novamente.',
};

const UNKNOWN_ERROR_TITLE = 'Erro ao processar a solicitação';
const UNKNOWN_ERROR_DETAIL = 'Tente novamente em instantes.';
const GENERIC_OPERATION_TITLE = 'Operação não concluída';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractTraceId(body: Record<string, unknown> | null): string | undefined {
  if (!body) return undefined;
  const extensions = body['extensions'];
  if (isPlainObject(extensions) && typeof extensions['traceId'] === 'string') {
    return extensions['traceId'];
  }
  if (typeof body['traceId'] === 'string') {
    return body['traceId'];
  }
  return undefined;
}

function fieldErrorsFromMap(errors: Record<string, unknown>): BackendFieldError[] {
  const result: BackendFieldError[] = [];
  for (const [field, value] of Object.entries(errors)) {
    if (Array.isArray(value)) {
      for (const msg of value) {
        if (typeof msg === 'string' && msg.length > 0) {
          result.push({ field, message: msg });
        }
      }
    } else if (typeof value === 'string' && value.length > 0) {
      result.push({ field, message: value });
    }
  }
  return result;
}

function fieldErrorsFromArray(arr: unknown[]): BackendFieldError[] {
  const result: BackendFieldError[] = [];
  for (const item of arr) {
    if (!isPlainObject(item)) continue;
    const field =
      typeof item['field'] === 'string'
        ? item['field']
        : typeof item['fieldName'] === 'string'
          ? (item['fieldName'] as string)
          : '';
    const message =
      typeof item['message'] === 'string'
        ? item['message']
        : typeof item['errorMessage'] === 'string'
          ? (item['errorMessage'] as string)
          : '';
    if (message) {
      result.push({ field, message });
    }
  }
  return result;
}

export function getBackendErrorMessage(err: HttpErrorResponse): BackendErrorMessage {
  if (!err || err.status === 0) {
    return NETWORK_ERROR;
  }

  const body = isPlainObject(err.error) ? err.error : null;

  if (!body) {
    return {
      title: UNKNOWN_ERROR_TITLE,
      detail: typeof err.message === 'string' && err.message ? err.message : UNKNOWN_ERROR_DETAIL,
    };
  }

  const traceId = extractTraceId(body);

  const bodyTitle = typeof body['title'] === 'string' ? body['title'].trim() : '';
  const bodyDetail = typeof body['detail'] === 'string' ? body['detail'].trim() : '';
  const bodyMessage = typeof body['message'] === 'string' ? body['message'].trim() : '';
  const bodyData = typeof body['data'] === 'string' ? body['data'].trim() : '';
  const bodyCode = typeof body['code'] === 'string' ? body['code'].trim() : '';

  const rawErrors = body['errors'];

  if (Array.isArray(rawErrors)) {
    const fieldErrors = fieldErrorsFromArray(rawErrors);
    const firstMessage = fieldErrors[0]?.message ?? '';
    return {
      title: bodyTitle || GENERIC_OPERATION_TITLE,
      detail: bodyDetail || bodyMessage || firstMessage || UNKNOWN_ERROR_DETAIL,
      ...(traceId ? { traceId } : {}),
      ...(fieldErrors.length > 0 ? { fieldErrors } : {}),
    };
  }

  if (isPlainObject(rawErrors)) {
    const fieldErrors = fieldErrorsFromMap(rawErrors);
    if (fieldErrors.some((e) => e.field.toLowerCase() === VAGA_SUSPENSA_CODIGO.toLowerCase())) {
      return {
        title: bodyTitle || GENERIC_OPERATION_TITLE,
        detail: VAGA_SUSPENSA_MENSAGEM,
        ...(traceId ? { traceId } : {}),
        fieldErrors,
      };
    }
    const firstMessage = fieldErrors[0]?.message ?? '';
    return {
      title: bodyTitle || GENERIC_OPERATION_TITLE,
      detail: bodyDetail || bodyMessage || firstMessage || UNKNOWN_ERROR_DETAIL,
      ...(traceId ? { traceId } : {}),
      ...(fieldErrors.length > 0 ? { fieldErrors } : {}),
    };
  }

  if (bodyCode && bodyMessage) {
    return {
      title: bodyTitle || GENERIC_OPERATION_TITLE,
      detail: bodyMessage,
      ...(traceId ? { traceId } : {}),
    };
  }

  const detail = bodyDetail || bodyMessage || bodyData || UNKNOWN_ERROR_DETAIL;
  return {
    title: bodyTitle || GENERIC_OPERATION_TITLE,
    detail,
    ...(traceId ? { traceId } : {}),
  };
}
